import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import './AdminModeration.css';

function AdminModeration() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, critical
  const [selectedReport, setSelectedReport] = useState(null);
  const [actionModal, setActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await apiFetch('/api/reports/moderation-queue', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.reports);
      } else {
        setMessage('Failed to load reports');
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      setMessage('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await apiFetch('/api/reports/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.platformStats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleReviewReport = async (reportId, status, action) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await apiFetch(`/api/reports/${reportId}/review`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          status,
          actionTaken: action,
          moderatorNotes: actionNotes
        }),
      });

      if (response.ok) {
        setMessage(`Report ${status} successfully`);
        setActionModal(false);
        setSelectedReport(null);
        setActionNotes('');
        fetchReports();
        fetchStats();
      } else {
        const data = await response.json();
        setMessage(data.error || 'Failed to review report');
      }
    } catch (error) {
      console.error('Error reviewing report:', error);
      setMessage('Failed to review report');
    }
  };

  const openActionModal = (report, type) => {
    setSelectedReport(report);
    setActionType(type);
    setActionModal(true);
  };

  const confirmAction = () => {
    if (!selectedReport) return;

    let status = 'resolved';
    let action = '';

    switch (actionType) {
      case 'warn':
        action = 'Warning sent to user';
        break;
      case 'suspend':
        action = 'User account suspended';
        status = 'resolved';
        break;
      case 'ban':
        action = 'User account banned';
        status = 'resolved';
        break;
      case 'dismiss':
        action = 'No action taken';
        status = 'dismissed';
        break;
      default:
        action = 'Reviewed';
    }

    handleReviewReport(selectedReport.id, status, action);
  };

  const filteredReports = reports.filter(report => {
    if (filter === 'pending') return report.status === 'pending';
    if (filter === 'critical') return report.priority === 'critical' && report.status === 'pending';
    return true;
  });

  const formatCategory = (category) => {
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: '#dc3545',
      high: '#fd7e14',
      normal: '#0d6efd',
      low: '#6c757d'
    };
    return colors[priority] || colors.normal;
  };

  if (loading) {
    return (
      <div className="admin-moderation-container">
        <div className="loading">Loading moderation queue...</div>
      </div>
    );
  }

  return (
    <div className="admin-moderation-container">
      <div className="admin-header">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back
        </button>
        <h1>🛡️ Admin Moderation</h1>
        <p>Review and take action on user reports</p>
      </div>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.totalReports}</div>
            <div className="stat-label">Total Reports</div>
          </div>
          <div className="stat-card pending">
            <div className="stat-value">{stats.pendingReports}</div>
            <div className="stat-label">Pending Review</div>
          </div>
          <div className="stat-card critical">
            <div className="stat-value">{stats.criticalReports}</div>
            <div className="stat-label">Critical</div>
          </div>
        </div>
      )}

      <div className="filter-tabs">
        <button 
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All Reports ({reports.length})
        </button>
        <button 
          className={filter === 'pending' ? 'active' : ''}
          onClick={() => setFilter('pending')}
        >
          Pending ({reports.filter(r => r.status === 'pending').length})
        </button>
        <button 
          className={filter === 'critical' ? 'active' : ''}
          onClick={() => setFilter('critical')}
        >
          Critical ({reports.filter(r => r.priority === 'critical' && r.status === 'pending').length})
        </button>
      </div>

      {message && (
        <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {filteredReports.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✓</div>
          <h2>No Reports to Review</h2>
          <p>All caught up! No {filter !== 'all' ? filter : ''} reports at this time.</p>
        </div>
      ) : (
        <div className="reports-list">
          {filteredReports.map((report) => (
            <div key={report.id} className="report-card">
              <div className="report-header">
                <div className="report-priority" style={{ backgroundColor: getPriorityColor(report.priority) }}>
                  {report.priority.toUpperCase()}
                </div>
                <div className="report-status">
                  {report.status}
                </div>
              </div>

              <div className="report-content">
                <div className="report-users">
                  <div className="user-info">
                    <strong>Reporter:</strong> {report.reporterName} (ID: {report.reporterId})
                  </div>
                  <div className="user-info reported">
                    <strong>Reported User:</strong> {report.reportedName} (ID: {report.reportedId})
                    {report.pendingReportsCount > 1 && (
                      <span className="multiple-reports">
                        ⚠️ {report.pendingReportsCount} pending reports
                      </span>
                    )}
                  </div>
                </div>

                <div className="report-details">
                  <div className="detail-row">
                    <strong>Category:</strong> {formatCategory(report.category)}
                  </div>
                  <div className="detail-row">
                    <strong>Details:</strong> {report.details}
                  </div>
                  <div className="detail-row">
                    <strong>Reported:</strong> {new Date(report.createdAt).toLocaleString()}
                  </div>
                </div>

                {report.status === 'pending' && (
                  <div className="action-buttons">
                    <button 
                      className="action-btn warn"
                      onClick={() => openActionModal(report, 'warn')}
                    >
                      Send Warning
                    </button>
                    <button 
                      className="action-btn suspend"
                      onClick={() => openActionModal(report, 'suspend')}
                    >
                      Suspend User
                    </button>
                    <button 
                      className="action-btn ban"
                      onClick={() => openActionModal(report, 'ban')}
                    >
                      Ban User
                    </button>
                    <button 
                      className="action-btn dismiss"
                      onClick={() => openActionModal(report, 'dismiss')}
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {actionModal && selectedReport && (
        <div className="modal-overlay" onClick={() => setActionModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Action</h2>
              <button onClick={() => setActionModal(false)} className="close-btn">
                ×
              </button>
            </div>

            <div className="modal-content">
              <p>
                <strong>Action:</strong> {actionType.charAt(0).toUpperCase() + actionType.slice(1)}
              </p>
              <p>
                <strong>User:</strong> {selectedReport.reportedName} (ID: {selectedReport.reportedId})
              </p>
              <p>
                <strong>Report:</strong> {formatCategory(selectedReport.category)}
              </p>

              <div className="form-group">
                <label>Moderator Notes (Optional)</label>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  placeholder="Add notes about this decision..."
                  rows="4"
                />
              </div>

              <div className="modal-actions">
                <button onClick={() => setActionModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={confirmAction} className="confirm-btn">
                  Confirm {actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminModeration;
